import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, FileDown, FileText, Trash2, FileSignature, ClipboardList, CheckCircle2, XCircle, LogOut, Lock, CalendarPlus, ShieldAlert, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminContracts() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  // Iter 57 — vehicle correction dialog. Fleet is lazy-loaded when the admin
  // opens the modal (no upfront cost when the page loads).
  const [fixContract, setFixContract] = useState(null);
  const [fleet, setFleet] = useState(null); // null = still fetching
  const [fixLoading, setFixLoading] = useState(false);
  const [fixPick, setFixPick] = useState("");
  // Iter 60 — swap audit trail: when did the swap actually happen and why.
  // Critical when the customer test-rode a scooter, didn't like it, swapped
  // to another and got a fine before/after the swap.
  const [fixSwapAt, setFixSwapAt] = useState("");
  const [fixSwapNote, setFixSwapNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/contracts", { params: q ? { q } : {} });
        if (!cancelled) setList(data);
      } catch (_) { /* noop */ }
      finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [q, reloadKey]);

  const downloadPdf = async (c) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contracts/${c.id}/pdf`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        // Try to surface the real backend error (e.g. missing field, template
        // corrupted). Otherwise fall back to the HTTP status.
        let detail = "";
        try {
          const j = await res.clone().json();
          detail = j?.detail || "";
        } catch {
          try { detail = await res.clone().text(); } catch { /* noop */ }
        }
        throw new Error(`PDF ${res.status}${detail ? " — " + String(detail).slice(0, 240) : ""}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto_${c.customer.surname}_${c.id.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const downloadPdfShort = async (c) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contracts/${c.id}/pdf-short`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        let detail = "";
        try {
          const j = await res.clone().json();
          detail = j?.detail || "";
        } catch {
          try { detail = await res.clone().text(); } catch { /* noop */ }
        }
        throw new Error(`PDF breve ${res.status}${detail ? " — " + String(detail).slice(0, 240) : ""}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto_BREVE_${c.customer.surname}_${c.id.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Iter 58/59 — attestation now opens a dialog that lets the admin adjust
  // the responsibility window before generating the PDF. Needed for legacy
  // contracts that weren't closed precisely or were extended manually before
  // the Iter 45 tool shipped.
  const [attContract, setAttContract] = useState(null);
  const [attStart, setAttStart] = useState("");
  const [attEnd, setAttEnd] = useState("");
  const [attLoading, setAttLoading] = useState(false);

  // Convert an ISO string to `YYYY-MM-DDTHH:MM` in **local** (Rome) time so
  // it fits into a `<input type="datetime-local">` without timezone offset.
  const isoToLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openAttestation = (c) => {
    setAttContract(c);
    setAttStart(isoToLocalInput(c.start_at));
    // Prefer closed_at when present (actual return time), else scheduled end_at.
    setAttEnd(isoToLocalInput(c.closed_at || c.end_at));
  };

  const closeAttestation = () => {
    setAttContract(null);
    setAttStart("");
    setAttEnd("");
    setAttLoading(false);
  };

  const submitAttestation = async () => {
    if (!attContract) return;
    setAttLoading(true);
    try {
      const params = new URLSearchParams();
      const origStart = isoToLocalInput(attContract.start_at);
      const origEnd = isoToLocalInput(attContract.closed_at || attContract.end_at);
      // Only send an override if the admin actually changed the field —
      // otherwise the backend applies the same defaults the automatic PDF
      // would have (23:59 for still-open, closed_at for closed).
      if (attStart && attStart !== origStart) {
        params.set("override_start", new Date(attStart).toISOString());
      }
      if (attEnd && attEnd !== origEnd) {
        params.set("override_end", new Date(attEnd).toISOString());
      }
      const url = `${BACKEND_URL}/api/admin/contracts/${attContract.id}/attestation${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) {
        let detail = "";
        try { detail = (await res.clone().json())?.detail || ""; } catch { /* noop */ }
        throw new Error(`Attestazione ${res.status}${detail ? " — " + detail : ""}`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `attestazione_${attContract.customer.surname}_${attContract.id.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      closeAttestation();
    } catch (e) {
      toast.error(e.message);
      setAttLoading(false);
    }
  };


  const remove = async (id) => {
    if (!window.confirm(isEN ? "Delete this contract?" : "Eliminare questo contratto?")) return;
    try {
      await api.delete(`/admin/contracts/${id}`);
      toast.success(isEN ? "Deleted" : "Eliminato");
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Iter 57 — vehicle correction: lazy-load the fleet the first time the
  // admin opens the modal, then keep it in memory for future clicks.
  const openFixVehicle = async (c) => {
    setFixContract(c);
    setFixPick("");
    // Default the swap timestamp to "now" in Rome time (browser local for
    // the desk) so the admin usually only needs to tweak minutes.
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    setFixSwapAt(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`);
    setFixSwapNote("");
    if (fleet === null) {
      try {
        const { data } = await api.get("/scooters");
        setFleet(Array.isArray(data) ? data : []);
      } catch (_) {
        setFleet([]);
      }
    }
  };

  const closeFixVehicle = () => {
    setFixContract(null);
    setFixPick("");
    setFixSwapAt("");
    setFixSwapNote("");
    setFixLoading(false);
  };

  const submitFixVehicle = async () => {
    if (!fixContract || !fixPick) return;
    const picked = (fleet || []).find((v) => v.id === fixPick);
    if (!picked) return;
    const msg = isEN
      ? `Change the vehicle on contract #${(fixContract.contract_number || fixContract.id.slice(0,8))} to "${picked.model} (${picked.plate})"?\nPrice and dates stay unchanged.`
      : `Correggere il veicolo del contratto #${(fixContract.contract_number || fixContract.id.slice(0,8))} in "${picked.model} (${picked.plate})"?\nPrezzo e date restano invariati.`;
    if (!window.confirm(msg)) return;
    setFixLoading(true);
    try {
      const body = { new_scooter_id: fixPick };
      // Only send override fields when the admin actually provided them.
      if (fixSwapAt) body.swap_at = new Date(fixSwapAt).toISOString();
      if (fixSwapNote.trim()) body.swap_note = fixSwapNote.trim();
      const r = await api.patch(`/admin/contracts/${fixContract.id}/vehicle`, body);
      toast.success((isEN ? "Vehicle corrected → " : "Veicolo corretto → ") + `${r.data?.new_model} (${r.data?.new_plate})`);
      closeFixVehicle();
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setFixLoading(false);
    }
  };

  const togglePayment = async (c) => {
    const next = c.payment_status === "pagato" ? "non_pagato" : "pagato";
    try {
      await api.patch(`/admin/contracts/${c.id}/payment`, { payment_status: next });
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const closeRental = async (c) => {
    if (!window.confirm(isEN ? "Close this rental? The vehicle will become available again." : "Chiudere il noleggio? Il veicolo tornerà disponibile.")) return;
    try {
      await api.post(`/admin/contracts/${c.id}/close`);
      toast.success(isEN ? "Rental closed" : "Noleggio chiuso");
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Iter 45 — extend rental (customer calls asking for more days). Opens a
  // dialog with a datetime input pre-filled at the current end_at. Server
  // rejects with 409 if any overlap on the same vehicle.
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendNewEnd, setExtendNewEnd] = useState("");
  const [extending, setExtending] = useState(false);

  const openExtendDialog = (c) => {
    setExtendTarget(c);
    // Bump default by +1 day from current end so admin only edits if needed.
    const cur = new Date(c.end_at);
    cur.setDate(cur.getDate() + 1);
    // datetime-local expects "YYYY-MM-DDTHH:MM" with no timezone
    const tzOffMin = cur.getTimezoneOffset();
    const local = new Date(cur.getTime() - tzOffMin * 60000);
    setExtendNewEnd(local.toISOString().slice(0, 16));
  };
  const submitExtend = async () => {
    if (!extendTarget || !extendNewEnd) return;
    setExtending(true);
    try {
      // Convert local datetime-local string → ISO with Z (server accepts ISO)
      const iso = new Date(extendNewEnd).toISOString();
      await api.post(`/admin/contracts/${extendTarget.id}/extend`, { new_end_at: iso });
      toast.success(isEN ? "Rental extended" : "Noleggio prolungato");
      setExtendTarget(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setExtending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-contracts-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">{isEN ? "Walk-in / Manual" : "Walk-in / Manuali"}</p>
          <h1 className="font-display text-4xl font-bold mt-1">{isEN ? "Contracts" : "Contratti"}</h1>
          <p className="text-zinc-400 text-sm mt-1">
            {isEN ? "Create and search rental contracts signed in-store on tablet." : "Crea e cerca i contratti di noleggio firmati in sede su tablet."}
          </p>
        </div>
        <Button onClick={() => navigate("/admin/contracts/new")} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="contracts-new-button">
          <Plus className="w-4 h-4 mr-1" />
          {isEN ? "New contract" : "Nuovo contratto"}
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={isEN ? "Search by name, surname, plate, model, document…" : "Cerca per nome, cognome, targa, modello, documento…"}
          className="bg-zinc-900 border-zinc-800 pl-9"
          data-testid="contracts-search-input"
        />
      </div>

      {/* Table */}
      <div className="border border-zinc-800 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900/40">
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">N°</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{isEN ? "Customer" : "Cliente"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{isEN ? "Vehicle" : "Veicolo"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{isEN ? "Period" : "Periodo"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{isEN ? "Total / Pay" : "Totale / Pag."}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{isEN ? "Reports" : "Verbali"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">{t("admin.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-zinc-500 text-center py-8">{t("common.loading")}</TableCell></TableRow>
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-zinc-500 text-center py-12">
                  <FileSignature className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  {isEN ? "No contracts yet" : "Nessun contratto"}
                </TableCell>
              </TableRow>
            ) : (
              list.map((c) => {
                const isClosed = c.rental_status === "chiuso";
                return (
                <TableRow
                  key={c.id}
                  className={`border-zinc-800 ${
                    isClosed
                      ? "bg-slate-800/70 hover:bg-slate-800/80 border-l-4 border-l-slate-500"
                      : "hover:bg-zinc-900/20"
                  }`}
                  data-testid={`contract-row-${c.id}`}
                >
                  <TableCell className="font-mono text-xs">
                    <div className={isClosed ? "text-slate-300" : "text-[#FFD700]"}>
                      {c.contract_number || c.id.slice(0,8).toUpperCase()}
                    </div>
                    {isClosed && (
                      <div className="mt-1 space-y-0.5">
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-600/40 border border-slate-400/40 text-[10px] font-bold uppercase tracking-wider text-slate-100"
                          data-testid={`contract-closed-badge-${c.id}`}
                        >
                          <Lock className="w-2.5 h-2.5" />
                          {isEN ? "Closed" : "Chiuso"}
                        </span>
                        {c.closed_at && (
                          <p className="text-[10px] text-slate-300" data-testid={`contract-closed-at-${c.id}`}>
                            {isEN ? "Returned" : "Rientrato"}: {new Date(c.closed_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}
                          </p>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="text-zinc-100 font-medium">{c.customer.name} {c.customer.surname}</p>
                    <p className="text-zinc-500 text-xs font-mono">{c.customer.document_number}</p>
                    <p className="text-zinc-500 text-xs">{c.customer.phone}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-zinc-100">{c.vehicle.model}</p>
                    <p className="text-zinc-500 text-xs font-mono">{c.vehicle.plate}</p>
                  </TableCell>
                  <TableCell className="text-xs text-zinc-300">
                    {c.start_at.slice(0, 10)} → {c.end_at.slice(0, 10)}
                    <span className="block text-zinc-500">{c.days}g</span>
                  </TableCell>
                  <TableCell>
                    <p className="text-[#FFD700] font-semibold">€{c.total_amount.toFixed(2)}</p>
                    <button onClick={() => togglePayment(c)}
                            className={`mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition ${
                              c.payment_status === "pagato"
                                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25"
                                : "bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25"
                            }`}
                            data-testid={`contract-pay-${c.id}`}>
                      {c.payment_status === "pagato" ? <><CheckCircle2 className="w-3 h-3" /> Pagato</> : <><XCircle className="w-3 h-3" /> Non pagato</>}
                    </button>
                  </TableCell>
                  <TableCell className="text-xs">
                    <Link to={`/admin/contracts/${c.id}/delivery`} data-testid={`contract-delivery-${c.id}`}
                          className={`flex items-center gap-1 ${c.delivery_report ? "text-emerald-400" : "text-zinc-500 hover:text-zinc-300"}`}>
                      <ClipboardList className="w-3 h-3" /> {isEN ? "Delivery" : "Consegna"}
                    </Link>
                    {!isClosed && (
                      <button
                        type="button"
                        onClick={() => openExtendDialog(c)}
                        className="flex items-center gap-1 mt-1 text-sky-400 hover:text-sky-300"
                        data-testid={`contract-extend-${c.id}`}
                      >
                        <CalendarPlus className="w-3 h-3" /> {isEN ? "Extend rental" : "Estensione RENT"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openAttestation(c)}
                      className="flex items-center gap-1 mt-1 text-orange-400 hover:text-orange-300"
                      title={isEN ? "Police attestation (for traffic fines)" : "Attestazione per polizia / multe"}
                      data-testid={`contract-attestation-${c.id}`}
                    >
                      <ShieldAlert className="w-3 h-3" /> {isEN ? "Attestation" : "Attestazione"}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(c)}
                            className="text-zinc-400 hover:text-[#FFD700] hover:bg-zinc-800"
                            title={isEN ? "Full PDF (3 pages)" : "PDF completo (3 pagine)"}
                            data-testid={`contract-pdf-${c.id}`}>
                      <FileDown className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadPdfShort(c)}
                            className="text-zinc-400 hover:text-amber-300 hover:bg-zinc-800"
                            title={isEN ? "Short PDF (1 page · desk print)" : "PDF breve (1 pagina · stampa banco)"}
                            data-testid={`contract-pdf-short-${c.id}`}>
                      <FileText className="w-4 h-4" />
                    </Button>
                    {c.rental_status !== "chiuso" && (
                      <Button size="sm" variant="ghost" onClick={() => closeRental(c)}
                              className="text-zinc-400 hover:text-amber-400 hover:bg-zinc-800"
                              title={isEN ? "Close rental" : "Chiudi noleggio"}
                              data-testid={`contract-close-${c.id}`}>
                        <LogOut className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openFixVehicle(c)}
                            className="text-zinc-400 hover:text-sky-300 hover:bg-zinc-800"
                            title={isEN ? "Fix vehicle (wrong plate)" : "Correggi veicolo (targa sbagliata)"}
                            data-testid={`contract-fix-vehicle-${c.id}`}>
                      <Wrench className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c.id)}
                            className="text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
                            data-testid={`contract-delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Iter 45 — Extend rental dialog */}
      <Dialog open={!!extendTarget} onOpenChange={(o) => !o && setExtendTarget(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="extend-rental-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-100">
              <CalendarPlus className="w-5 h-5 text-sky-400" />
              {isEN ? "Extend rental" : "Estensione noleggio"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {isEN
                ? "Prolong an active contract without recreating it. Any overlap with other bookings is blocked."
                : "Prolunga un contratto attivo senza rifarlo. Sovrapposizioni con altre prenotazioni bloccate."}
            </DialogDescription>
          </DialogHeader>
          {extendTarget && (
            <div className="space-y-3 py-2">
              <div className="text-sm text-zinc-400">
                {isEN ? "Contract" : "Contratto"}{" "}
                <span className="font-mono text-[#FFD700]">{extendTarget.contract_number || extendTarget.id.slice(0, 8).toUpperCase()}</span>
                {" · "}
                <span className="text-zinc-100">{extendTarget.customer?.name} {extendTarget.customer?.surname}</span>
              </div>
              <div className="rounded-lg bg-zinc-900/50 border border-zinc-800 p-3 text-xs text-zinc-300 space-y-1">
                <div>{isEN ? "Vehicle" : "Veicolo"}: <span className="font-mono">{extendTarget.vehicle?.model} · {extendTarget.vehicle?.plate}</span></div>
                <div>{isEN ? "Current end" : "Fine attuale"}: <span className="text-amber-300">{new Date(extendTarget.end_at).toLocaleString("it-IT")}</span></div>
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">{isEN ? "New return date/time *" : "Nuova data/ora riconsegna *"}</Label>
                <Input
                  type="datetime-local"
                  value={extendNewEnd}
                  onChange={(e) => setExtendNewEnd(e.target.value)}
                  className="bg-zinc-950 border-zinc-800 mt-1"
                  min={new Date(extendTarget.end_at).toISOString().slice(0, 16)}
                  data-testid="extend-rental-new-end"
                />
                <p className="text-[11px] text-zinc-500 mt-1">
                  {isEN
                    ? "Days and total will be recalculated. If the new window overlaps another booking on this vehicle, the request is rejected."
                    : "Giorni e totale saranno ricalcolati. Se la nuova finestra si sovrappone a un'altra prenotazione del veicolo, l'estensione viene rifiutata."}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setExtendTarget(null)} data-testid="extend-rental-cancel">
              {isEN ? "Cancel" : "Annulla"}
            </Button>
            <Button
              onClick={submitExtend}
              disabled={extending || !extendNewEnd}
              className="bg-sky-500 hover:bg-sky-400 text-black font-semibold"
              data-testid="extend-rental-confirm"
            >
              {extending ? "…" : (isEN ? "Extend" : "Prolunga")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 57 — Fix vehicle dialog (typo correction on already-signed contract) */}
      <Dialog open={!!attContract} onOpenChange={(o) => !o && closeAttestation()}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="attestation-dialog">
          <DialogHeader>
            <DialogTitle className="text-orange-400 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              {isEN ? "Print attestation (for police / traffic fines)" : "Stampa attestazione (per polizia / multe)"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 space-y-1">
              {attContract && (
                <span className="block">
                  {isEN ? "Contract" : "Contratto"}{" "}
                  <span className="text-zinc-200 font-semibold">
                    #{attContract.contract_number || attContract.id?.slice(0, 8)}
                  </span>
                  {" · "}
                  <span className="text-zinc-200">
                    {(attContract.vehicle || {}).model} ({(attContract.vehicle || {}).plate})
                  </span>
                </span>
              )}
              <span className="block text-[11px] text-amber-300">
                {isEN
                  ? "Adjust the responsibility window if needed. This only affects the printed PDF — the underlying contract is not modified."
                  : "Regola qui il periodo di responsabilità del cliente. La modifica viene applicata SOLO al PDF stampato — il contratto non viene toccato."}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">
                {isEN ? "Rental start (Rome time)" : "Inizio noleggio (orario Roma)"}
              </Label>
              <Input
                type="datetime-local"
                value={attStart}
                onChange={(e) => setAttStart(e.target.value)}
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="attestation-start-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">
                {isEN ? "Responsibility end (Rome time)" : "Fine responsabilità cliente (orario Roma)"}
              </Label>
              <Input
                type="datetime-local"
                value={attEnd}
                onChange={(e) => setAttEnd(e.target.value)}
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="attestation-end-input"
              />
              <p className="text-[10px] text-zinc-500 mt-1">
                {isEN
                  ? "Tip: for legacy contracts closed imprecisely, set this to the true return time (e.g. 23:59 of the return day)."
                  : "Suggerimento: per vecchi contratti chiusi in modo impreciso, imposta l'ora reale di rientro (es. 23:59 del giorno di rientro)."}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={closeAttestation} disabled={attLoading} data-testid="attestation-cancel">
              {isEN ? "Cancel" : "Annulla"}
            </Button>
            <Button
              onClick={submitAttestation}
              disabled={attLoading || !attStart || !attEnd}
              className="bg-orange-500 hover:bg-orange-400 text-black font-semibold"
              data-testid="attestation-generate"
            >
              {attLoading ? "…" : (isEN ? "Generate PDF" : "Genera PDF")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!fixContract} onOpenChange={(o) => !o && closeFixVehicle()}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="fix-vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FFD700]">
              {isEN ? "Correct vehicle on contract" : "Correggi veicolo del contratto"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 space-y-1">
              {fixContract && (
                <>
                  <span className="block">
                    {isEN ? "Contract" : "Contratto"}{" "}
                    <span className="text-zinc-200 font-semibold">
                      #{fixContract.contract_number || fixContract.id?.slice(0, 8)}
                    </span>
                    {" · "}
                    <span className="text-zinc-200">
                      {(fixContract.vehicle || {}).model} ({(fixContract.vehicle || {}).plate})
                    </span>
                  </span>
                  <span className="block text-[11px] text-amber-300">
                    {isEN
                      ? "Price, dates, customer and signature stay unchanged — this is a pure identity fix."
                      : "Prezzo, date, cliente e firma restano invariati — corregge solo il veicolo."}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Label className="text-xs text-zinc-400">
              {isEN ? "Pick the correct vehicle from the fleet" : "Scegli il veicolo corretto dalla flotta"}
            </Label>
            {fleet === null ? (
              <div className="text-center text-zinc-500 py-6 text-sm">{t("common.loading")}</div>
            ) : (
              <select
                value={fixPick}
                onChange={(e) => setFixPick(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-2 text-sm text-zinc-100 focus:outline-none focus:border-sky-500"
                data-testid="fix-vehicle-select"
              >
                <option value="">
                  {isEN ? "— Select a vehicle —" : "— Seleziona un veicolo —"}
                </option>
                {(fleet || [])
                  .filter((v) => v.id !== ((fixContract && fixContract.vehicle) || {}).scooter_id)
                  .sort((a, b) => (a.model || "").localeCompare(b.model || ""))
                  .map((v) => (
                    <option key={v.id} value={v.id} data-testid={`fix-vehicle-opt-${v.id}`}>
                      {(v.vehicle_type === "auto" ? "🚗 " : "🛵 ")}
                      {v.model} — {v.plate}
                      {v.displacement ? ` (${v.displacement}cc)` : ""}
                    </option>
                  ))}
              </select>
            )}

            {/* Iter 60 — swap audit trail (fine attribution per plate) */}
            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <p className="text-[11px] text-amber-300 leading-relaxed">
                {isEN
                  ? "Log WHEN the swap happened + WHY. If a traffic fine arrives later, the attestation PDF will split the responsibility per plate + time window."
                  : "Registra QUANDO è avvenuto il cambio + PERCHÉ. Se arriva una multa, l'attestazione dividerà la responsabilità per targa + fascia oraria."}
              </p>
              <div>
                <Label className="text-xs text-zinc-400">
                  {isEN ? "Swap time (Rome time)" : "Ora del cambio (orario Roma)"}
                </Label>
                <Input
                  type="datetime-local"
                  value={fixSwapAt}
                  onChange={(e) => setFixSwapAt(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="fix-vehicle-swap-at"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">
                  {isEN ? "Reason / note" : "Motivo / nota"}
                </Label>
                <Input
                  type="text"
                  value={fixSwapNote}
                  onChange={(e) => setFixSwapNote(e.target.value)}
                  placeholder={isEN ? "e.g. Customer test-rode and didn't like it" : "es. Cliente non gradiva la Vespa, presa la Burgman"}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="fix-vehicle-swap-note"
                  maxLength={200}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={closeFixVehicle} disabled={fixLoading} data-testid="fix-vehicle-cancel">
              {isEN ? "Cancel" : "Annulla"}
            </Button>
            <Button
              onClick={submitFixVehicle}
              disabled={fixLoading || !fixPick}
              className="bg-sky-500 hover:bg-sky-400 text-black font-semibold"
              data-testid="fix-vehicle-confirm"
            >
              {fixLoading ? "…" : (isEN ? "Fix vehicle" : "Correggi veicolo")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
